import { useCallback, useState } from "react";
import { Layout, Page, Stack, Text } from "@shopify/polaris";
import StepsButtonActions from "../../../../../common/StepsButtonActions/StepsButtonActions.js";
import { Field, Form } from "react-final-form";
import SelectVariantCard from "./components/SelectVariantCard.js";
import TagsSelectionSection from "./components/TagsSelectionSection/TagsSelectionSection.js";
import SpecificSelectionSection from "./components/SpecificSelectionSection/SpecificSelectionSection.js";
import AllProductsSection from "./components/AllProductsSection/AllProductsSection.js";
import {
  SELECT_PRODUCTS_BASED_ON_ALL,
  SELECT_PRODUCTS_BASED_ON_SPECIFIC,
  SELECT_PRODUCTS_BASED_ON_TAGS,
} from "../../../../../../../constants/selectProductsBasedOn.js";
import { updateStockSelectedProducts } from "../../../../../../store/features/action.js";
import { validation } from "./validation.js";
import { useAppDispatch, useAppSelector } from "../../../../../../hooks/redux.js";
import { useShopQuery } from "../../../../../../hooks/useShopQuery.js";
import { updateShop } from "../../../../../../store/features/shop.js";
import useHandleKeyDown from "../../../../../../hooks/useHandleKeyDown.js";
import UsedProductsBanner from "../../../../../common/UsedProductsBanner/UsedProductsBanner.js";

import ValidationErrorBanner from "../../../../../common/ValidationErrorBanner/ValidationErrorBanner.js";
import { useAppMutation } from "../../../../../../hooks/useAppMutation.js";
import checkProductLimits from "../../../../../../utils/checkProductLimits.js";
import { removeDuplicateObjects } from "../../../../../../../helpers/removeDuplicateObjects.js";
import FetchRemainingProducts from "./components/SpecificSelectionSection/components/FetchRemainingProducts.js";
import { getHeaderText, getSubtitleText } from "../../../../../../utils/productSelectionTextUtils.js";

const StockSelectProducts = () => {
  const dispatch = useAppDispatch();
  const stockSelectedProducts = useAppSelector((state) => state.action.actionData.selectedProducts.stockSelectedProducts);
  const currentActionId = useAppSelector((state) => state.action.actionId);
  const [productsCount, setProductsCount] = useState(0);
  const [isProductUsed, setIsProductUsed] = useState(false);
  const [isBannerChecked, setIsBannerChecked] = useState(false);
  const [bannerValidError, setBannerValidError] = useState(false);
  const [filterValues, setFilterValues] = useState({});
  const [totalFetched, setTotalFetched] = useState(0);
  const [totalCount, setTotalCount] = useState(null);

  const { isFetching, refetch } = useShopQuery({
    onSuccess: (shopData) => dispatch(updateShop(shopData)),
    enabled: false,
  });

  const { mutate: fetchRemainingProducts, isLoading: isFetchingRemainingProducts } = useAppMutation({
    url: "/api/products/filters",
    reactMutationOptions: {
      onSuccess: (data, variables) => {
        const updatedProducts = [...variables.currentProducts, ...data.products];
        setTotalFetched(updatedProducts.length);

        if (data.products.length === variables.take) {
          fetchRemainingProducts({
            ...variables,
            skip: variables.skip + variables.take,
            currentProducts: updatedProducts,
          });
        } else {
          const uniqueArray = removeDuplicateObjects(updatedProducts, "id");
          dispatch(updateStockSelectedProducts({
            ...variables.formValues,
            selectedSpecificItems: uniqueArray,
          }));
          checkProductLimits(variables.shopData, variables.formValues, uniqueArray, null, dispatch);
        }
      },
    },
  });

  const onSubmit = useCallback(async (data) => {
    if (!isBannerChecked && isProductUsed) {
      setBannerValidError(true)
      return;
    }

    const { data: shopData } = await refetch();
    const { selectedSpecificItems } = data;

    if (Object.keys(filterValues).length > 0 && totalCount !== selectedSpecificItems.length) {
      fetchRemainingProducts({
        ...filterValues,
        skip: totalFetched,
        take: 1200,
        currentProducts: selectedSpecificItems,
        formValues: data,
        shopData,
      });
    } else {
      dispatch(updateStockSelectedProducts(data));
      if (shopData) {
        checkProductLimits(shopData, data, selectedSpecificItems, productsCount, dispatch);
      }
    }
  }, [dispatch, refetch, productsCount, filterValues, totalCount, totalFetched, isBannerChecked, isProductUsed]);

  const handleKeyDown = useHandleKeyDown();

  return (
    <Page fullWidth>
      {isProductUsed && (
        <UsedProductsBanner
          setIsBannerChecked={setIsBannerChecked}
          setIsProductUsed={setIsProductUsed}
          bannerValidError={bannerValidError}
          setBannerValidError={setBannerValidError}
        />
      )}
      {isFetchingRemainingProducts ? (
        <FetchRemainingProducts totalFetched={totalFetched} totalToFetch={totalCount} />
      ) : (
        <Form
          onSubmit={onSubmit}
          mutators={{
            setFormAttribute: ([fieldName, fieldVal], state, { changeValue }) => {
              changeValue(state, fieldName, () => fieldVal);
            },
          }}
          initialValues={stockSelectedProducts}
          validate={validation}
          render={({ form, handleSubmit, values, hasValidationErrors, submitFailed }) => {
            const shouldShowBanner = hasValidationErrors && submitFailed;

            return (
              <form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
                <Stack vertical>
                  <ValidationErrorBanner
                    message="Select at least one product before proceeding to the next step."
                    show={shouldShowBanner}
                  />
                  <StepsButtonActions disabled={false} isNextLoading={isFetching} />
                  <Layout>
                    <Layout.Section secondary>
                      <Stack vertical>
                        <Text variant="headingLg" as="p">
                          Select your products
                        </Text>
                        <Text variant="bodySm" as="p">
                          Pick a selection method and let Stockhide know which products you’d like to include in your action.
                        </Text>
                      </Stack>
                    </Layout.Section>

                    <Layout.Section>
                      <Field
                        name="selectProductsBasedOn"
                        component={SelectVariantCard}
                        setFilterValues={setFilterValues}
                        values={values}
                        form={form}
                      />
                    </Layout.Section>
                  </Layout>
                  <Layout>
                    <Layout.Section secondary>
                      <Stack vertical>
                        <Text variant="headingLg" as="p">
                          {getHeaderText(values.selectProductsBasedOn)}
                        </Text>
                        <Text variant="bodySm" as="p">
                          {getSubtitleText(values.selectProductsBasedOn)}
                        </Text>
                      </Stack>
                    </Layout.Section>
                    <Layout.Section>
                      {values.selectProductsBasedOn === SELECT_PRODUCTS_BASED_ON_SPECIFIC && (
                        <Field
                          name="selectedSpecificItems"
                          component={SpecificSelectionSection}
                          values={values}
                          setFilterValues={setFilterValues}
                          filterValues={filterValues}
                          totalFetched={totalFetched}
                          setTotalFetched={setTotalFetched}
                          totalCount={totalCount}
                          setTotalCount={setTotalCount}
                          currentActionId={currentActionId}
                          setIsProductUsed={setIsProductUsed}
                        />
                      )}
                      {values.selectProductsBasedOn === SELECT_PRODUCTS_BASED_ON_ALL && (
                        <AllProductsSection values={values} currentActionId={currentActionId} setIsProductUsed={setIsProductUsed} />
                      )}
                      {values.selectProductsBasedOn === SELECT_PRODUCTS_BASED_ON_TAGS && (
                        <TagsSelectionSection
                          setFormAttribute={form.mutators.setFormAttribute}
                          values={values}
                          productsCount={productsCount}
                          setProductsCount={setProductsCount}
                          currentActionId={currentActionId}
                          setIsProductUsed={setIsProductUsed}
                        />
                      )}
                    </Layout.Section>
                  </Layout>
                </Stack>
              </form>
            );
          }}
        />
      )}
    </Page>
  );
};

export default StockSelectProducts;